import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';
import { normalizeVideoViewCrop } from './types';
import type { VideoViewCrop } from './types';
import {
  CropPoint,
  DEFAULT_VIDEO_VIEW_CROP_SELECTION,
  FULL_FRAME_VIDEO_VIEW_CROP,
  RectLike,
  VideoCropResizeHandle,
  clampVideoViewCrop,
  getContentCropFromVisualCrop,
  getContentPointFromClientPoint,
  getMediaContentRect,
  getVideoViewCropFromPoints,
  getVideoViewCropStyle,
  getVisualCropFromContentCrop,
  moveVideoViewCrop,
  resizeVideoViewCrop
} from './cropViewMath';

interface VideoCropEditorOverlayProps {
  stageRef: React.RefObject<HTMLElement | null>;
  value: VideoViewCrop;
  aspectRatio?: number | null;
  isMirrored?: boolean;
  hasAppliedCrop?: boolean;
  onChange: (crop: VideoViewCrop) => void;
  onApply: () => void;
  onReset: () => void;
  onCancel: () => void;
}

type CropEditorFrame = {
  stageRect: RectLike;
  contentRect: RectLike;
  frameStyle: CSSProperties;
};

type CropPointerInteraction = {
  mode: 'create' | 'move' | 'resize';
  handle?: VideoCropResizeHandle;
  pointerStart: CropPoint;
  initialVisualCrop: VideoViewCrop;
  contentRect: RectLike;
};

const RESIZE_HANDLES: Array<{ handle: VideoCropResizeHandle; label: string }> = [
  { handle: 'nw', label: 'Resize crop box top left' },
  { handle: 'n', label: 'Resize crop box top edge' },
  { handle: 'ne', label: 'Resize crop box top right' },
  { handle: 'e', label: 'Resize crop box right edge' },
  { handle: 'se', label: 'Resize crop box bottom right' },
  { handle: 's', label: 'Resize crop box bottom edge' },
  { handle: 'sw', label: 'Resize crop box bottom left' },
  { handle: 'w', label: 'Resize crop box left edge' }
];

const KEYBOARD_NUDGE = 0.01;
const KEYBOARD_LARGE_NUDGE = 0.05;

const toRectLike = (rect: DOMRect): RectLike => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height
});

const getKeyboardStep = (event: React.KeyboardEvent): number => (
  event.shiftKey ? KEYBOARD_LARGE_NUDGE : KEYBOARD_NUDGE
);

const getKeyboardMoveDelta = (event: React.KeyboardEvent): CropPoint | null => {
  const step = getKeyboardStep(event);

  if (event.key === 'ArrowLeft') return { x: -step, y: 0 };
  if (event.key === 'ArrowRight') return { x: step, y: 0 };
  if (event.key === 'ArrowUp') return { x: 0, y: -step };
  if (event.key === 'ArrowDown') return { x: 0, y: step };

  return null;
};

const getHandleKeyboardPoint = (
  crop: VideoViewCrop,
  handle: VideoCropResizeHandle,
  event: React.KeyboardEvent
): CropPoint | null => {
  const step = getKeyboardStep(event);
  const point: CropPoint = {
    x: handle.includes('w')
      ? crop.x
      : handle.includes('e')
      ? crop.x + crop.width
      : crop.x + (crop.width / 2),
    y: handle.includes('n')
      ? crop.y
      : handle.includes('s')
      ? crop.y + crop.height
      : crop.y + (crop.height / 2)
  };

  if (event.key === 'ArrowLeft' && (handle.includes('w') || handle.includes('e'))) {
    return { ...point, x: point.x - step };
  }
  if (event.key === 'ArrowRight' && (handle.includes('w') || handle.includes('e'))) {
    return { ...point, x: point.x + step };
  }
  if (event.key === 'ArrowUp' && (handle.includes('n') || handle.includes('s'))) {
    return { ...point, y: point.y - step };
  }
  if (event.key === 'ArrowDown' && (handle.includes('n') || handle.includes('s'))) {
    return { ...point, y: point.y + step };
  }

  return null;
};

const isPrimaryPointerButton = (event: React.PointerEvent): boolean => (
  event.button === 0 || event.pointerType !== 'mouse'
);

const VideoCropEditorOverlay: React.FC<VideoCropEditorOverlayProps> = ({
  stageRef,
  value,
  aspectRatio,
  isMirrored = false,
  hasAppliedCrop = false,
  onChange,
  onApply,
  onReset,
  onCancel
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [frame, setFrame] = useState<CropEditorFrame | null>(null);
  const [activeInteraction, setActiveInteraction] = useState<CropPointerInteraction['mode'] | null>(null);
  const cleanupPointerListenersRef = useRef<(() => void) | null>(null);
  const normalizedContentCrop = useMemo(
    () => clampVideoViewCrop(value ?? DEFAULT_VIDEO_VIEW_CROP_SELECTION),
    [value]
  );
  const visualCrop = useMemo(
    () => getVisualCropFromContentCrop(normalizedContentCrop, isMirrored),
    [isMirrored, normalizedContentCrop]
  );

  const publishVisualCrop = useCallback((nextVisualCrop: VideoViewCrop) => {
    onChange(getContentCropFromVisualCrop(nextVisualCrop, isMirrored));
  }, [isMirrored, onChange]);

  const measureFrame = useCallback(() => {
    const stage = stageRef.current ?? overlayRef.current?.parentElement;
    if (!stage) {
      setFrame(null);
      return;
    }

    const stageRect = toRectLike(stage.getBoundingClientRect());
    const contentRect = getMediaContentRect(stageRect, aspectRatio);
    const left = Math.max(0, contentRect.left - stageRect.left);
    const top = Math.max(0, contentRect.top - stageRect.top);

    setFrame({
      stageRect,
      contentRect,
      frameStyle: {
        left: `${left}px`,
        top: `${top}px`,
        width: `${Math.max(contentRect.width, 0)}px`,
        height: `${Math.max(contentRect.height, 0)}px`
      }
    });
  }, [aspectRatio, stageRef]);

  useLayoutEffect(() => {
    measureFrame();
  }, [measureFrame]);

  useEffect(() => {
    const stage = stageRef.current ?? overlayRef.current?.parentElement;
    let resizeObserver: ResizeObserver | null = null;

    if (stage && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measureFrame);
      resizeObserver.observe(stage);
    }

    window.addEventListener('resize', measureFrame);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureFrame);
    };
  }, [measureFrame, stageRef]);

  useEffect(() => {
    return () => {
      cleanupPointerListenersRef.current?.();
      cleanupPointerListenersRef.current = null;
    };
  }, []);

  const updateFromPointerInteraction = useCallback((
    interaction: CropPointerInteraction,
    clientX: number,
    clientY: number
  ) => {
    const pointerPoint = getContentPointFromClientPoint(clientX, clientY, interaction.contentRect);

    if (interaction.mode === 'create') {
      publishVisualCrop(getVideoViewCropFromPoints(interaction.pointerStart, pointerPoint));
      return;
    }

    if (interaction.mode === 'move') {
      const deltaX = pointerPoint.x - interaction.pointerStart.x;
      const deltaY = pointerPoint.y - interaction.pointerStart.y;
      publishVisualCrop(moveVideoViewCrop(interaction.initialVisualCrop, deltaX, deltaY));
      return;
    }

    if (interaction.handle) {
      publishVisualCrop(resizeVideoViewCrop(
        interaction.initialVisualCrop,
        interaction.handle,
        pointerPoint
      ));
    }
  }, [publishVisualCrop]);

  const beginPointerInteraction = useCallback((
    event: React.PointerEvent<HTMLElement>,
    interaction: CropPointerInteraction
  ) => {
    if (!isPrimaryPointerButton(event)) return;

    event.preventDefault();
    event.stopPropagation();
    cleanupPointerListenersRef.current?.();

    const pointerTarget = event.currentTarget;
    pointerTarget.setPointerCapture?.(event.pointerId);
    setActiveInteraction(interaction.mode);
    updateFromPointerInteraction(interaction, event.clientX, event.clientY);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      updateFromPointerInteraction(interaction, moveEvent.clientX, moveEvent.clientY);
    };

    const handlePointerUp = () => {
      setActiveInteraction(null);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
      cleanupPointerListenersRef.current = null;
    };

    cleanupPointerListenersRef.current = handlePointerUp;
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  }, [updateFromPointerInteraction]);

  const handleCreatePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!frame || frame.contentRect.width <= 0 || frame.contentRect.height <= 0) return;

    const pointerStart = getContentPointFromClientPoint(event.clientX, event.clientY, frame.contentRect);
    beginPointerInteraction(event, {
      mode: 'create',
      pointerStart,
      initialVisualCrop: FULL_FRAME_VIDEO_VIEW_CROP,
      contentRect: frame.contentRect
    });
  }, [beginPointerInteraction, frame]);

  const handleMovePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!frame || frame.contentRect.width <= 0 || frame.contentRect.height <= 0) return;

    beginPointerInteraction(event, {
      mode: 'move',
      pointerStart: getContentPointFromClientPoint(event.clientX, event.clientY, frame.contentRect),
      initialVisualCrop: visualCrop,
      contentRect: frame.contentRect
    });
  }, [beginPointerInteraction, frame, visualCrop]);

  const handleResizePointerDown = useCallback((
    handle: VideoCropResizeHandle,
    event: React.PointerEvent<HTMLButtonElement>
  ) => {
    if (!frame || frame.contentRect.width <= 0 || frame.contentRect.height <= 0) return;

    beginPointerInteraction(event, {
      mode: 'resize',
      handle,
      pointerStart: getContentPointFromClientPoint(event.clientX, event.clientY, frame.contentRect),
      initialVisualCrop: visualCrop,
      contentRect: frame.contentRect
    });
  }, [beginPointerInteraction, frame, visualCrop]);

  const handleSelectionKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onApply();
      return;
    }

    const delta = getKeyboardMoveDelta(event);
    if (!delta) return;

    event.preventDefault();
    event.stopPropagation();
    publishVisualCrop(moveVideoViewCrop(visualCrop, delta.x, delta.y));
  }, [onApply, onCancel, publishVisualCrop, visualCrop]);

  const handleResizeKeyDown = useCallback((
    handle: VideoCropResizeHandle,
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onApply();
      return;
    }

    const point = getHandleKeyboardPoint(visualCrop, handle, event);
    if (!point) return;

    event.preventDefault();
    event.stopPropagation();
    publishVisualCrop(resizeVideoViewCrop(visualCrop, handle, point));
  }, [onApply, onCancel, publishVisualCrop, visualCrop]);

  const overlayReady = Boolean(frame && frame.contentRect.width > 0 && frame.contentRect.height > 0);
  const hasDraftCrop = Boolean(normalizeVideoViewCrop(normalizedContentCrop));

  return (
    <div
      ref={overlayRef}
      className={`video-crop-editor-overlay ${overlayReady ? 'is-ready' : 'is-measuring'} ${activeInteraction ? `is-${activeInteraction}` : ''}`}
      data-testid="video-crop-editor-overlay"
      data-crop-mirrored={isMirrored ? 'true' : 'false'}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      aria-label="Crop view box editor"
    >
      <div
        id="crop-view-editor-shell"
        className={`crop-editor-hud ${hasAppliedCrop ? 'has-crop' : 'is-empty'} ${hasDraftCrop ? 'has-draft-crop' : 'is-full-frame-draft'}`}
        role="group"
        aria-label="Crop view editor"
        data-testid="crop-editor-hud"
        data-crop-active={hasAppliedCrop ? 'true' : 'false'}
        data-draft-crop-active={hasDraftCrop ? 'true' : 'false'}
      >
        <button
          type="button"
          className="crop-editor-hud-button is-apply"
          onClick={onApply}
          title="Apply crop view"
          aria-label="Apply crop view"
          data-testid="crop-editor-apply-button"
        >
          <Check size={16} />
          <span>Apply</span>
        </button>
        <button
          type="button"
          className="crop-editor-hud-button is-reset"
          onClick={onReset}
          title="Reset crop view"
          aria-label="Reset crop view"
          data-testid="crop-editor-reset-button"
        >
          <RotateCcw size={16} />
          <span>Reset</span>
        </button>
        <button
          type="button"
          className="crop-editor-hud-button is-cancel"
          onClick={onCancel}
          title="Cancel crop edit"
          aria-label="Cancel crop edit"
          data-testid="crop-editor-cancel-button"
        >
          <X size={16} />
          <span>Cancel</span>
        </button>
      </div>
      {frame && (
        <div
          className="video-crop-editor-content-frame"
          data-testid="video-crop-editor-content-frame"
          style={frame.frameStyle}
          onPointerDown={handleCreatePointerDown}
        >
          <div
            className={`video-crop-editor-selection ${activeInteraction === 'move' ? 'is-moving' : ''}`}
            data-testid="video-crop-editor-selection"
            role="button"
            tabIndex={0}
            aria-label="Move crop selection"
            style={getVideoViewCropStyle(visualCrop)}
            onPointerDown={handleMovePointerDown}
            onKeyDown={handleSelectionKeyDown}
            onClick={(event) => event.stopPropagation()}
          >
            <span className="video-crop-editor-selection-grid" aria-hidden="true" />
            {RESIZE_HANDLES.map(({ handle, label }) => (
              <button
                key={handle}
                type="button"
                className={`video-crop-editor-resize-handle is-${handle}`}
                data-testid={`video-crop-editor-resize-${handle}`}
                aria-label={label}
                onPointerDown={(event) => handleResizePointerDown(handle, event)}
                onKeyDown={(event) => handleResizeKeyDown(handle, event)}
                onClick={(event) => event.stopPropagation()}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCropEditorOverlay;
